import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getStakingAlerts, getStakingHealth, getChainStakingStats, getStakingVolumeSummary, getStakingDailySummary, getChainStakingHistory } from '../api';
import { useIsMobile } from '../hooks/useIsMobile';

const CHAIN_COLOR = { ethereum: '#818cf8', bsc: '#f0b90b', solana: '#22d3ee', near: '#a3e635' };

const CHAIN_LABEL = { ethereum: 'Ethereum', bsc: 'BNB Chain', solana: 'Solana', near: 'NEAR Protocol' };
const CHAIN_STATS_META = {
  ethereum: {
    symbol: 'Ξ', name: 'Ethereum',
    note: (t, s) => t('staking.ethereumNote', { count: s.validatorCount ?? '—' }),
  },
  bsc: {
    symbol: 'B', name: 'BNB Chain',
    note: (t, s) => t('staking.bscNote', { count: s.validatorCount ?? '—' }),
  },
  solana: {
    symbol: '◎', name: 'Solana',
    note: (t, s) => t('staking.solanaNote', { count: s.validatorCount ?? '—' }),
  },
  near: {
    symbol: 'N', name: 'NEAR Protocol',
    note: (t, s) => t('staking.nearNote', { count: s.validatorCount ?? '—' }),
  },
  tron: {
    symbol: 'T', name: 'TRON',
    note: (t) => t('staking.aggregateOnlyNote'),
  },
  cardano: {
    symbol: '₳', name: 'Cardano',
    note: (t) => t('staking.aggregateOnlyNote'),
  },
  avalanche: {
    symbol: 'A', name: 'Avalanche',
    note: (t, s) => t('staking.avalancheNote', { count: s.validatorCount ?? '—' }),
  },
};
// Aggregate-only chains (total staked + % of supply, refreshed every 30 min)
// — no per-transaction event tracking, so they're excluded from the alert
// feed, chain filter, listener health row, and flow-trend chart, which all
// assume a StakeEvent stream exists. See their stats services for why.
const STATS_ONLY_CHAINS = ['tron', 'cardano', 'avalanche'];
const DIRECTION = {
  stake:   { labelKey: 'staking.directionStake', color: '#10b981', emoji: '⬆️' },
  unstake: { labelKey: 'staking.directionUnstake', color: '#dc2626', emoji: '⬇️' },
};

function fmtUsd(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
  return `$${n.toFixed(0)}`;
}

function fmtAmount(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  return n.toLocaleString('en-US', { maximumFractionDigits: 4 });
}

function fmtAssetAmount(n, symbol) {
  if (n == null || !Number.isFinite(n)) return '—';
  return `${(n / 1e6).toLocaleString('en-US', { maximumFractionDigits: 2 })}M ${symbol}`;
}

function fmtPct(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  return `${n.toFixed(2)}%`;
}

function fmtTime(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('vi-VN', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function shortAddr(a) {
  if (!a) return '—';
  return a.length > 14 ? `${a.slice(0, 6)}…${a.slice(-4)}` : a;
}

const TX_EXPLORER = {
  ethereum: (tx) => `https://etherscan.io/tx/${tx}`,
  bsc:      (tx) => `https://bscscan.com/tx/${tx}`,
  solana:   (tx) => `https://solscan.io/tx/${tx}`,
  near:     (tx) => {
    // Check if it's a synthetic ID (starts with "near:") or real transaction hash
    if (tx.startsWith('near:')) {
      return null; // Don't link to explorer for synthetic IDs
    }
    return `https://pikespeak.ai/transaction-viewer/${tx}`;
  },
};

function txExplorerUrl(chain, txHash) {
  const build = TX_EXPLORER[chain];
  return build && txHash ? build(txHash) : null;
}

export default function StakingPage() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [alerts, setAlerts] = useState([]);
  const [health, setHealth] = useState({});
  const [chainStats, setChainStats] = useState({});
  const [volumeSummary, setVolumeSummary] = useState(null);
  const [summaryDays, setSummaryDays] = useState(7);
  const [loading, setLoading] = useState(true);
  const [chainFilter, setChainFilter] = useState('');
  const [dailyChain, setDailyChain] = useState('ethereum');
  const [historyDays, setHistoryDays] = useState(14);
  const [dailyRows, setDailyRows] = useState(null);
  const [ratioDays, setRatioDays] = useState(30);
  const [ratioRows, setRatioRows] = useState(null);
  const [activeTab, setActiveTab] = useState('overview'); // 'overview' | 'history'

  // "Tổng quan" tab data — polled every 30s regardless of which tab is
  // active, since it's the default landing view.
  const load = useCallback(async () => {
    try {
      const [{ alerts }, { chains }, statsRes, summaryRes] = await Promise.all([
        getStakingAlerts({ days: 7, chain: chainFilter || undefined }),
        getStakingHealth(),
        getChainStakingStats().catch(() => ({})),
        getStakingVolumeSummary(summaryDays).catch(() => null),
      ]);
      setAlerts(alerts || []);
      setHealth(chains || {});
      setChainStats(statsRes || {});
      setVolumeSummary(summaryRes);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [chainFilter, summaryDays]);

  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  // "Lịch sử & Biểu đồ" tab data — daily rollups + supply-ratio history.
  // These only change on their own cron schedule (hours/once-a-day), not
  // worth polling every 30s, and — the actual fix here — only fetched once
  // the tab is actually opened instead of on every page load regardless of
  // which tab the user is looking at.
  useEffect(() => {
    if (activeTab !== 'history') return;
    let active = true;
    Promise.all([
      getStakingDailySummary(historyDays, dailyChain).catch(() => null),
      getChainStakingHistory(ratioDays).catch(() => null),
    ]).then(([dailyRes, ratioRes]) => {
      if (!active) return;
      setDailyRows(dailyRes ? dailyRes.rows : null);
      setRatioRows(ratioRes ? ratioRes.rows : null);
    });
    return () => { active = false; };
  }, [activeTab, dailyChain, historyDays, ratioDays]);

  const pageStyle = isMobile ? { ...s.page, padding: '16px 12px' } : s.page;
  const chains = ['ethereum', 'bsc', 'solana', 'near'];
  const stakeAlerts = alerts.filter((a) => a.direction === 'stake');
  const unstakeAlerts = alerts.filter((a) => a.direction === 'unstake');

  return (
    <div style={pageStyle}>
      <div style={s.header}>
        <div>
          <h2 style={s.title}>{t('staking.title')}</h2>
          <p style={s.subtitle}>
            {t('staking.subtitle')}
          </p>
        </div>
      </div>

      <div style={s.tabBar}>
        <button
          onClick={(e) => { setActiveTab('overview'); e.currentTarget.blur(); }}
          style={activeTab === 'overview' ? s.tabBtnActive : s.tabBtn}
        >
          {t('staking.tabOverview')}
        </button>
        <button
          onClick={(e) => { setActiveTab('history'); e.currentTarget.blur(); }}
          style={activeTab === 'history' ? s.tabBtnActive : s.tabBtn}
        >
          {t('staking.tabHistory')}
        </button>
      </div>

      {activeTab === 'overview' ? (
        <>
          <div style={s.statsRow}>
            {chains.map((c) => (
              <ChainStakingStatsCard
                key={c} chain={c} stats={chainStats[c]}
                selected={chainFilter === c}
                onClick={() => setChainFilter((prev) => (prev === c ? '' : c))}
              />
            ))}
            {STATS_ONLY_CHAINS.map((c) => <ChainStakingStatsCard key={c} chain={c} stats={chainStats[c]} />)}
          </div>

          <VolumeSummaryPanel summary={volumeSummary} days={summaryDays} onDaysChange={setSummaryDays} />

          <div style={s.healthRow}>
            {chains.map((c) => <HealthChip key={c} chain={c} h={health[c]} />)}
          </div>

          {!chainFilter ? (
            <p style={s.pickChainHint}>{t('staking.pickChainHint')}</p>
          ) : (
            <>
              <div style={s.filters}>
                <span style={s.filterActiveLabel}>{t('staking.filterActiveFor', { chain: CHAIN_LABEL[chainFilter] })}</span>
                <button style={s.btnGhostSmall} onClick={() => setChainFilter('')}>{t('staking.closeDetail')}</button>
              </div>

              {loading ? (
                <div style={s.loading}>{t('technical.loadingData')}</div>
              ) : alerts.length === 0 ? (
                <div style={s.empty}>
                  <div style={{ fontSize: 48, marginBottom: 12 }}>🥩</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>
                    {t('staking.emptyAlertsTitle')}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--text-muted)', maxWidth: 460, textAlign: 'center' }}>
                    {t('staking.emptyAlertsDesc')}
                  </div>
                </div>
              ) : (
                <div style={isMobile ? s.sectionsStacked : s.sectionsRow}>
                  <DirectionSection direction="stake" alerts={stakeAlerts} isMobile={isMobile} />
                  <DirectionSection direction="unstake" alerts={unstakeAlerts} isMobile={isMobile} />
                </div>
              )}
            </>
          )}
        </>
      ) : (
        <>
          <DailyHistoryPanel
            rows={dailyRows}
            chain={dailyChain}
            onChainChange={setDailyChain}
            historyDays={historyDays}
            onHistoryDaysChange={setHistoryDays}
            chains={chains}
          />

          <FlowTrendChart rows={dailyRows} chain={dailyChain} />

          <SupplyRatioChart rows={ratioRows} days={ratioDays} onDaysChange={setRatioDays} chains={chains} />
        </>
      )}
    </div>
  );
}

// `onClick` is only passed for chains that actually have a StakeEvent alert
// feed (see the two separate .map() calls above) — the aggregate-only cards
// (STATS_ONLY_CHAINS) render the same component without it, so they stay
// non-interactive rather than looking clickable with nothing to show.
function ChainStakingStatsCard({ chain, stats, selected, onClick }) {
  const { t } = useTranslation();
  const meta = CHAIN_STATS_META[chain];
  const clickable = !!onClick;
  const cardStyle = {
    ...s.statsCard,
    ...(clickable ? { cursor: 'pointer' } : {}),
    ...(selected ? s.statsCardSelected : {}),
  };
  if (!stats) {
    return (
      <div style={cardStyle} onClick={onClick}>
        <div style={s.statsHeader}>
          <span style={{ fontSize: 18 }}>{meta.symbol}</span>
          <span style={{ fontWeight: 800, color: 'var(--text)', fontSize: 14 }}>{meta.name}</span>
        </div>
        <p style={{ ...s.statsNote, borderTop: 'none', paddingTop: 0 }}>{t('staking.loadingFirstData')}</p>
      </div>
    );
  }
  return (
    <div style={cardStyle} onClick={onClick}>
      <div style={s.statsHeader}>
        <span style={{ fontSize: 18 }}>{meta.symbol}</span>
        <span style={{ fontWeight: 800, color: 'var(--text)', fontSize: 14 }}>{meta.name}</span>
        {stats.updatedAt && <span style={s.statsUpdated}>{fmtTime(stats.updatedAt)}</span>}
      </div>
      <div style={s.statsGrid}>
        <Cell label={t('staking.totalStaked', { asset: stats.asset?.toUpperCase() })} value={fmtAssetAmount(stats.totalStaked, stats.asset?.toUpperCase())} />
        <Cell label={t('staking.pctTotalSupply')} value={fmtPct(stats.pctOfTotalSupply)} color="#10b981" />
        <Cell label={t('staking.pctCirculating')} value={fmtPct(stats.pctOfCirculating)} color="#10b981" />
      </div>
      <p style={s.statsNote}>* {meta.note(t, stats)}</p>
      {clickable && <p style={s.statsClickHint}>{selected ? t('staking.clickHintSelected') : t('staking.clickHintView')}</p>}
    </div>
  );
}

// Aggregated USD in/out (stake vs unstake) across ALL tracked assets, summed
// over every decoded event in the window (not just the large ones that
// became alerts) — so this reflects true total flow, not the filtered
// large-transaction subset shown in the tables below.
function VolumeSummaryPanel({ summary, days, onDaysChange }) {
  const { t } = useTranslation();
  if (!summary) return null;
  const netColor = summary.totalNetUsd >= 0 ? '#10b981' : '#dc2626';
  return (
    <div style={s.statsCard}>
      <div style={s.statsHeader}>
        <span style={{ fontSize: 18 }}>💵</span>
        <span style={{ fontWeight: 800, color: 'var(--text)', fontSize: 14 }}>{t('staking.volumeSummaryTitle')}</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          {[7, 14, 30].map((d) => (
            <button
              key={d}
              onClick={(e) => { onDaysChange(d); e.currentTarget.blur(); }}
              style={days === d ? s.filterBtnActive : s.filterBtn}
            >
              {t('staking.daysBtn', { count: d })}
            </button>
          ))}
        </div>
      </div>

      <div style={s.statsGrid}>
        <Cell label={t('staking.totalIn')} value={fmtUsd(summary.totalInUsd)} color="#10b981" />
        <Cell label={t('staking.totalOut')} value={fmtUsd(summary.totalOutUsd)} color="#dc2626" />
        <Cell label={t('staking.netInOut')} value={fmtUsd(summary.totalNetUsd)} color={netColor} />
      </div>

      {summary.perAsset.length > 0 && (
        <div style={{ marginTop: 10, borderTop: '1px solid var(--border)', paddingTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {summary.perAsset.map((a) => {
            const aNetColor = a.netUsd >= 0 ? '#10b981' : '#dc2626';
            return (
              <div key={a.asset} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 11 }}>
                <span style={{ fontWeight: 700, color: 'var(--text)', minWidth: 40 }}>{a.asset}</span>
                <span style={{ color: '#10b981' }}>↑ {fmtUsd(a.inUsd)}</span>
                <span style={{ color: '#dc2626' }}>↓ {fmtUsd(a.outUsd)}</span>
                <span style={{ color: aNetColor, fontWeight: 600 }}>{t('staking.netLabel')} {fmtUsd(a.netUsd)}</span>
                <span style={{ color: 'var(--text-dim)', marginLeft: 'auto' }}>{t('staking.ordersCountSuffix', { count: a.inCount + a.outCount })}</span>
              </div>
            );
          })}
        </div>
      )}
      <p style={s.statsNote}>{t('staking.volumeSummaryNote', { days })}</p>
    </div>
  );
}

// One column per "chốt sổ" day (stakingRollupCron finalizes yesterday every
// day at 00:20 UTC): a paired stake/unstake bar scaled to that chain's own
// max day in the window, plus the net USD figure — lets a user see at a
// glance which recent days skewed net-in vs net-out for a given chain.
function DailyHistoryPanel({ rows, chain, onChainChange, historyDays, onHistoryDaysChange, chains }) {
  const { t } = useTranslation();
  const maxVal = rows && rows.length > 0
    ? Math.max(1, ...rows.map((r) => Math.max(r.stakeAmountUsd, r.unstakeAmountUsd)))
    : 1;
  return (
    <div style={s.statsCard}>
      <div style={s.statsHeader}>
        <span style={{ fontSize: 18 }}>📅</span>
        <span style={{ fontWeight: 800, color: 'var(--text)', fontSize: 14 }}>{t('staking.dailyHistoryTitle')}</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {chains.map((c) => (
              <button key={c} onClick={(e) => { onChainChange(c); e.currentTarget.blur(); }} style={chain === c ? s.filterBtnActive : s.filterBtn}>
                {CHAIN_LABEL[c]}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {[14, 30, 60].map((d) => (
              <button key={d} onClick={(e) => { onHistoryDaysChange(d); e.currentTarget.blur(); }} style={historyDays === d ? s.filterBtnActive : s.filterBtn}>
                {t('staking.daysBtn', { count: d })}
              </button>
            ))}
          </div>
        </div>
      </div>

      {!rows ? (
        <p style={s.statsNote}>{t('staking.loadingEllipsis')}</p>
      ) : rows.length === 0 ? (
        <p style={s.statsNote}>
          {t('staking.noSettledDays', { chain: CHAIN_LABEL[chain] })}
        </p>
      ) : (
        <>
          <div style={s.dailyChart}>
            {rows.map((r) => {
              const stakeH = Math.max(2, Math.round((r.stakeAmountUsd / maxVal) * 100));
              const unstakeH = Math.max(2, Math.round((r.unstakeAmountUsd / maxVal) * 100));
              const netColor = r.netAmountUsd >= 0 ? '#10b981' : '#dc2626';
              return (
                <div
                  key={r.date}
                  style={s.dailyCol}
                  title={t('staking.dailyTooltip', {
                    date: r.date, stake: fmtUsd(r.stakeAmountUsd), stakeCount: r.stakeCount,
                    unstake: fmtUsd(r.unstakeAmountUsd), unstakeCount: r.unstakeCount,
                    net: fmtUsd(r.netAmountUsd), large: r.largeEventCount, total: r.totalEventCount,
                  })}
                >
                  <div style={s.dailyBars}>
                    <div style={{ ...s.dailyBar, height: `${stakeH}%`, background: '#10b981' }} />
                    <div style={{ ...s.dailyBar, height: `${unstakeH}%`, background: '#dc2626' }} />
                  </div>
                  <span style={{ fontSize: 9, fontWeight: 700, color: netColor }}>
                    {r.netAmountUsd >= 0 ? '+' : ''}{fmtUsd(r.netAmountUsd)}
                  </span>
                  <span style={s.dailyDate}>{r.date.slice(5)}</span>
                </div>
              );
            })}
          </div>
          <div style={{ display: 'flex', gap: 14, fontSize: 10, color: 'var(--text-muted)', marginTop: 10 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ ...s.legendDot, background: '#10b981' }} /> {t('staking.legendStakeIn')}</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ ...s.legendDot, background: '#dc2626' }} /> {t('staking.legendUnstakeOut')}</span>
          </div>
          <p style={s.statsNote}>
            {t('staking.dailyHistoryNote')}
          </p>
        </>
      )}
    </div>
  );
}

// Small multi-series SVG line chart. `series` = [{ name, color, points: [{x: 'YYYY-MM-DD', y: number, estimated?: bool}] }]
// x-axis is the union of every series' dates, sorted — series don't need to
// share the exact same date set (a chain missing a day just breaks its line
// there instead of misaligning the others). A point marked `estimated`
// renders as a hollow ring instead of a filled dot, so a value derived by
// backfilling (not a real direct reading) reads as visually distinct.
function LineChart({ series, height = 150, formatY = (v) => v }) {
  const allPoints = series.flatMap((s) => s.points);
  if (allPoints.length === 0) return null;

  const xs = [...new Set(allPoints.map((p) => p.x))].sort();
  const ys = allPoints.map((p) => p.y);
  const maxY = Math.max(...ys, 0);
  const minY = Math.min(...ys, 0);
  const rangeY = maxY - minY || 1;

  const width = Math.max(360, xs.length * 30);
  const padL = 6, padR = 6, padT = 10, padB = 22;
  const plotW = width - padL - padR;
  const plotH = height - padT - padB;
  const xIndex = new Map(xs.map((x, i) => [x, i]));
  const xPos = (x) => padL + (xs.length > 1 ? (xIndex.get(x) / (xs.length - 1)) * plotW : plotW / 2);
  const yPos = (y) => padT + plotH - ((y - minY) / rangeY) * plotH;
  const labelEvery = Math.max(1, Math.ceil(xs.length / 8));

  return (
    <div style={{ overflowX: 'auto' }}>
      <svg width={width} height={height} style={{ display: 'block' }}>
        {minY < 0 && maxY > 0 && (
          <line x1={padL} x2={width - padR} y1={yPos(0)} y2={yPos(0)} stroke="var(--border)" strokeDasharray="3,3" />
        )}
        {series.map((s) => {
          const byX = new Map(s.points.map((p) => [p.x, p]));
          const present = xs.filter((x) => byX.has(x));
          if (present.length === 0) return null;
          const path = present.map((x, i) => `${i === 0 ? 'M' : 'L'}${xPos(x).toFixed(1)},${yPos(byX.get(x).y).toFixed(1)}`).join(' ');
          return (
            <g key={s.name}>
              <path d={path} fill="none" stroke={s.color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
              {present.map((x) => {
                const p = byX.get(x);
                const title = p.estimated ? `${s.name} · ${x} (ước tính): ${formatY(p.y)}` : `${s.name} · ${x}: ${formatY(p.y)}`;
                return (
                  <g key={x}>
                    {/* The visible dot (r=2.5) is too small a hit target to reliably
                        hover — a much larger invisible circle underneath carries the
                        actual tooltip, so hovering anywhere near the point works. */}
                    <circle cx={xPos(x)} cy={yPos(p.y)} r={9} fill="transparent" stroke="none">
                      <title>{title}</title>
                    </circle>
                    {p.estimated ? (
                      <circle cx={xPos(x)} cy={yPos(p.y)} r={2.5} fill="var(--surface, #0b0f1a)" stroke={s.color} strokeWidth={1.5} pointerEvents="none" />
                    ) : (
                      <circle cx={xPos(x)} cy={yPos(p.y)} r={2.5} fill={s.color} pointerEvents="none" />
                    )}
                  </g>
                );
              })}
            </g>
          );
        })}
        {xs.map((x, i) => (i % labelEvery === 0 ? (
          <text key={x} x={xPos(x)} y={height - 4} fontSize={9} fill="var(--text-dim)" textAnchor="middle">{x.slice(5)}</text>
        ) : null))}
      </svg>
    </div>
  );
}

// Trend line of daily stake-in vs unstake-out (USD) for one chain, over the
// same window/rows already loaded for the bar chart above — no extra fetch.
function FlowTrendChart({ rows, chain }) {
  const { t } = useTranslation();
  const series = rows ? [
    { name: t('staking.legendStakeIn'), color: '#10b981', points: rows.map((r) => ({ x: r.date, y: r.stakeAmountUsd })) },
    { name: t('staking.legendUnstakeOut'), color: '#dc2626', points: rows.map((r) => ({ x: r.date, y: r.unstakeAmountUsd })) },
  ] : [];
  return (
    <div style={s.statsCard}>
      <div style={s.statsHeader}>
        <span style={{ fontSize: 18 }}>📈</span>
        <span style={{ fontWeight: 800, color: 'var(--text)', fontSize: 14 }}>{t('staking.flowTrendTitle', { chain: CHAIN_LABEL[chain] })}</span>
      </div>
      {!rows || rows.length < 2 ? (
        <p style={s.statsNote}>{t('staking.flowTrendNeedMoreData')}</p>
      ) : (
        <>
          <LineChart series={series} formatY={fmtUsd} />
          <div style={{ display: 'flex', gap: 14, fontSize: 10, color: 'var(--text-muted)', marginTop: 8 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ ...s.legendDot, background: '#10b981' }} /> {t('staking.legendStakeInUsd')}</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ ...s.legendDot, background: '#dc2626' }} /> {t('staking.legendUnstakeOutUsd')}</span>
          </div>
        </>
      )}
    </div>
  );
}

// Trend line of %-of-total-supply staked, one line per chain, over time —
// backed by the daily chain-stats snapshot cron (a point-in-time reading,
// not a sum, so it can't be derived from StakeEvent/StakeDailySummary).
function SupplyRatioChart({ rows, days, onDaysChange, chains }) {
  const { t } = useTranslation();
  const byChain = new Map();
  (rows || []).forEach((r) => {
    if (!byChain.has(r.chain)) byChain.set(r.chain, []);
    if (r.pctOfTotalSupply != null) byChain.get(r.chain).push({ x: r.date, y: r.pctOfTotalSupply, estimated: !!r.estimated });
  });
  const series = chains
    .filter((c) => (byChain.get(c) || []).length > 0)
    .map((c) => ({ name: CHAIN_LABEL[c], color: CHAIN_COLOR[c], points: byChain.get(c) }));
  const totalPoints = series.reduce((sum, s) => sum + s.points.length, 0);

  return (
    <div style={s.statsCard}>
      <div style={s.statsHeader}>
        <span style={{ fontSize: 18 }}>📊</span>
        <span style={{ fontWeight: 800, color: 'var(--text)', fontSize: 14 }}>{t('staking.supplyRatioTitle')}</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {[30, 60, 90].map((d) => (
            <button key={d} onClick={(e) => { onDaysChange(d); e.currentTarget.blur(); }} style={days === d ? s.filterBtnActive : s.filterBtn}>
              {t('staking.daysBtn', { count: d })}
            </button>
          ))}
        </div>
      </div>
      {totalPoints < 2 ? (
        <p style={s.statsNote}>
          {t('staking.supplyRatioNeedMoreData', { count: totalPoints })}
        </p>
      ) : (
        <>
          <LineChart series={series} formatY={(v) => `${v.toFixed(2)}%`} />
          <div style={{ display: 'flex', gap: 14, fontSize: 10, color: 'var(--text-muted)', marginTop: 8, flexWrap: 'wrap' }}>
            {chains.map((c) => (
              <span key={c} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ ...s.legendDot, background: CHAIN_COLOR[c] }} /> {CHAIN_LABEL[c]}
              </span>
            ))}
          </div>
          <p style={s.statsNote}>
            {t('staking.supplyRatioNote')}
          </p>
        </>
      )}
    </div>
  );
}

function DirectionSection({ direction, alerts, isMobile }) {
  const { t } = useTranslation();
  const dir = DIRECTION[direction];
  const dirLabel = t(dir.labelKey);
  return (
    <div style={s.section}>
      <div style={s.sectionTitle}>
        <span style={{ ...s.badge, color: dir.color, borderColor: dir.color + '55', background: dir.color + '14' }}>
          {dir.emoji} {dirLabel}
        </span>
        <span style={s.sectionCount}>{t('staking.sectionCountLabel', { count: alerts.length })}</span>
      </div>
      {alerts.length === 0 ? (
        <div style={s.sectionEmpty}>{t('staking.sectionEmpty', { direction: dirLabel.toLowerCase() })}</div>
      ) : isMobile ? (
        <div style={s.cards}>
          {alerts.map((a) => <AlertCard key={a._id} a={a} />)}
        </div>
      ) : (
        <div style={s.tableWrap}>
          <div style={s.thead}>
            <span style={{ ...s.th, flex: 1.2 }}>{t('staking.colTime')}</span>
            <span style={s.th}>{t('staking.colChain')}</span>
            <span style={{ ...s.th, flex: 1.4 }}>{t('staking.colProtocol')}</span>
            <span style={{ ...s.th, flex: 1.3 }}>{t('staking.colAmount')}</span>
            <span style={s.th}>{t('staking.colUsdValue')}</span>
            <span style={{ ...s.th, flex: 1.6 }}>{t('staking.colAddress')}</span>
            <span style={{ ...s.th, flex: 1.6 }}>{t('staking.colTx')}</span>
          </div>
          {alerts.map((a) => (
            <div key={a._id} style={s.row}>
              <span style={{ ...s.td, flex: 1.2 }}>{fmtTime(a.createdAt)}</span>
              <span style={s.td}>{CHAIN_LABEL[a.chain] || a.chain}</span>
              <span style={{ ...s.td, flex: 1.4, fontWeight: 600, color: 'var(--text)' }}>{a.protocol}</span>
              <span style={{ ...s.td, flex: 1.3, fontWeight: 700, color: 'var(--text)' }}>
                {fmtAmount(a.amount)} {a.asset}
              </span>
              <span style={s.td}>{fmtUsd(a.amountUsd)}</span>
              <span style={{ ...s.td, flex: 1.6 }} title={a.actor}>{shortAddr(a.actor)}</span>
              <span style={{ ...s.td, flex: 1.6 }}>
                {txExplorerUrl(a.chain, a.txHash) ? (
                  <a href={txExplorerUrl(a.chain, a.txHash)} target="_blank" rel="noopener noreferrer" title={a.txHash} style={s.txLink}>
                    {shortAddr(a.txHash)} ↗
                  </a>
                ) : (
                  <span title={a.txHash}>{shortAddr(a.txHash)}</span>
                )}
              </span>
            </div>
          ))}
          <p style={s.note}>{t('staking.tableNote')}</p>
        </div>
      )}
    </div>
  );
}

function HealthChip({ chain, h }) {
  const { t } = useTranslation();
  const connected = h && h.connected;
  const stale = h && h.secondsSinceLastBlock != null && h.secondsSinceLastBlock > 120;
  const color = !connected ? 'var(--text-dim)' : stale ? '#f59e0b' : '#10b981';
  const label = !connected ? t('staking.healthDisconnected') : stale ? t('staking.healthStale') : t('staking.healthListening');
  return (
    <div style={s.chip}>
      <span style={{ ...s.dot, background: color }} />
      <span style={{ fontWeight: 700, color: 'var(--text)' }}>{CHAIN_LABEL[chain]}</span>
      <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>{label}</span>
    </div>
  );
}

function AlertCard({ a }) {
  const { t } = useTranslation();
  const dir = DIRECTION[a.direction] || DIRECTION.stake;
  return (
    <div style={s.card}>
      <div style={s.cardTop}>
        <span style={s.cardName}>{a.protocol} <span style={s.ticker}>{CHAIN_LABEL[a.chain] || a.chain}</span></span>
        <span style={{ ...s.badge, color: dir.color, borderColor: dir.color + '55', background: dir.color + '14' }}>
          {dir.emoji} {t(dir.labelKey)}
        </span>
      </div>
      <div style={s.cardGrid}>
        <Cell label={t('staking.cardAmount')} value={`${fmtAmount(a.amount)} ${a.asset}`} />
        <Cell label={t('staking.cardUsdValue')} value={fmtUsd(a.amountUsd)} />
        <Cell label={t('staking.cardTime')} value={fmtTime(a.createdAt)} />
        <Cell label={t('staking.cardAddress')} value={shortAddr(a.actor)} />
      </div>
      {txExplorerUrl(a.chain, a.txHash) ? (
        <a href={txExplorerUrl(a.chain, a.txHash)} target="_blank" rel="noopener noreferrer" title={a.txHash} style={{ ...s.cardMeta, ...s.txLink }}>
          {t('staking.cardTx')} {shortAddr(a.txHash)} ↗
        </a>
      ) : (
        <span style={s.cardMeta} title={a.txHash}>{t('staking.cardTx')} {shortAddr(a.txHash)}</span>
      )}
    </div>
  );
}

function Cell({ label, value, color }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
      <span style={{ fontSize: 9, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 700, color: color || 'var(--text)', wordBreak: 'break-word' }}>{value}</span>
    </div>
  );
}

const s = {
  page: { padding: '24px 32px', maxWidth: 1200, margin: '0 auto' },
  header: { marginBottom: 20 },
  title: { margin: '0 0 4px', fontSize: 20, fontWeight: 800, color: 'var(--text)' },
  subtitle: { margin: 0, fontSize: 13, color: 'var(--text-muted)' },
  loading: { color: 'var(--text-muted)', textAlign: 'center', padding: 60, fontSize: 14 },
  empty: { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '70px 20px' },

  tabBar: { display: 'flex', gap: 6, marginBottom: 18, borderBottom: '1px solid var(--border)' },
  // Two fully self-contained objects (every property always set in both) —
  // used as a straight either/or replacement, never merged/spread together.
  // Avoids any risk of a leftover property surviving a switch between them.
  tabBtn: {
    fontSize: 13, fontWeight: 500, color: 'var(--text-dim)', background: 'transparent',
    border: 'none', outline: 'none', boxShadow: 'none', textDecoration: 'none',
    borderBottomWidth: 2, borderBottomStyle: 'solid', borderBottomColor: 'transparent',
    padding: '9px 4px', marginBottom: -1, cursor: 'pointer',
  },
  tabBtnActive: {
    fontSize: 13, fontWeight: 700, color: 'var(--accent)', background: 'transparent',
    border: 'none', outline: 'none', boxShadow: 'none', textDecoration: 'none',
    borderBottomWidth: 2, borderBottomStyle: 'solid', borderBottomColor: 'var(--accent)',
    padding: '9px 4px', marginBottom: -1, cursor: 'pointer',
  },

  statsRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12, marginBottom: 16 },
  statsCard: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '16px 18px', minWidth: 0, transition: 'border-color 0.15s, box-shadow 0.15s' },
  statsCardSelected: { borderColor: 'var(--accent)', boxShadow: '0 0 0 1px var(--accent)' },
  statsHeader: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' },
  statsUpdated: { marginLeft: 'auto', fontSize: 10, color: 'var(--text-dim)' },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 12, marginBottom: 10 },
  statsNote: { fontSize: 10.5, color: 'var(--text-dim)', lineHeight: 1.6, margin: 0, borderTop: '1px solid var(--border)', paddingTop: 10 },
  statsClickHint: { fontSize: 10, color: 'var(--accent)', fontWeight: 700, margin: '8px 0 0', textAlign: 'center' },

  pickChainHint: { fontSize: 12.5, color: 'var(--text-muted)', background: 'var(--surface)', border: '1px dashed var(--border)', borderRadius: 10, padding: '14px 16px', textAlign: 'center', marginBottom: 4 },
  filterActiveLabel: { fontSize: 12.5, fontWeight: 700, color: 'var(--text)' },
  btnGhostSmall: { background: 'transparent', color: 'var(--text-dim)', border: '1px solid var(--border)', borderRadius: 8, padding: '4px 10px', fontSize: 11.5, fontWeight: 600, cursor: 'pointer' },

  dailyChart: { display: 'flex', alignItems: 'flex-end', gap: 6, height: 140, overflowX: 'auto', padding: '4px 2px 0', borderTop: '1px solid var(--border)', paddingTop: 14 },
  dailyCol: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, minWidth: 30, flexShrink: 0, cursor: 'default' },
  dailyBars: { display: 'flex', alignItems: 'flex-end', gap: 2, height: 90 },
  dailyBar: { width: 9, borderRadius: '3px 3px 0 0', transition: 'height 0.2s' },
  dailyDate: { fontSize: 9, color: 'var(--text-dim)' },
  legendDot: { width: 7, height: 7, borderRadius: '50%', display: 'inline-block' },

  healthRow: { display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 },
  chip: { display: 'flex', alignItems: 'center', gap: 6, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '6px 12px', fontSize: 12 },
  dot: { width: 7, height: 7, borderRadius: '50%', flexShrink: 0 },

  filters: { display: 'flex', gap: 20, flexWrap: 'wrap', marginBottom: 20 },
  filterGroup: { display: 'flex', flexDirection: 'column', gap: 4 },
  filterLabel: { fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 },
  // Two fully self-contained objects, always used as a straight either/or
  // replacement (never spread/merged) — same fix as the page tabs above:
  // a spread like {...filterBtn, ...(active ? filterBtnActive : {})} risks a
  // property from the active state surviving when a button goes back to
  // inactive, since only the KEYS present in filterBtnActive get reset.
  filterBtn: {
    fontSize: 11, fontWeight: 600, color: 'var(--text-dim)', background: 'var(--surface)',
    outline: 'none', boxShadow: 'none',
    borderWidth: 1, borderStyle: 'solid', borderColor: 'var(--border)',
    borderRadius: 8, padding: '5px 10px', cursor: 'pointer',
  },
  filterBtnActive: {
    fontSize: 11, fontWeight: 600, color: 'var(--accent)', background: 'var(--accent)14',
    outline: 'none', boxShadow: 'none',
    borderWidth: 1, borderStyle: 'solid', borderColor: 'var(--accent)',
    borderRadius: 8, padding: '5px 10px', cursor: 'pointer',
  },

  sectionsRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, alignItems: 'start' },
  sectionsStacked: { display: 'flex', flexDirection: 'column' },
  section: { marginBottom: 22, minWidth: 0 },
  sectionTitle: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 },
  sectionCount: { fontSize: 11, color: 'var(--text-muted)' },
  sectionEmpty: { fontSize: 12, color: 'var(--text-dim)', padding: '16px 14px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12 },

  tableWrap: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '8px 14px 14px', overflowX: 'auto' },
  thead: { display: 'flex', gap: 12, padding: '10px 8px', borderBottom: '1px solid var(--border)', marginBottom: 4 },
  th: { flex: 1, fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', letterSpacing: 0.7, textTransform: 'uppercase', minWidth: 0 },
  row: { display: 'flex', gap: 12, alignItems: 'center', padding: '11px 8px', borderBottom: '1px solid var(--border)', minWidth: 0 },
  td: { flex: 1, fontSize: 12, color: 'var(--text-dim)', minWidth: 0, wordBreak: 'break-word', overflowWrap: 'break-word' },
  ticker: { fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, marginLeft: 6 },
  badge: { fontSize: 10, fontWeight: 700, border: '1px solid', borderRadius: 8, padding: '3px 9px', whiteSpace: 'nowrap', display: 'inline-block' },
  note: { fontSize: 10, color: 'var(--text-dim)', margin: '10px 0 0' },

  cards: { display: 'flex', flexDirection: 'column', gap: 10 },
  card: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 14, display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0 },
  cardTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' },
  cardName: { fontSize: 14, fontWeight: 700, color: 'var(--text)', wordBreak: 'break-word' },
  cardGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, minWidth: 0 },
  cardMeta: { fontSize: 10, color: 'var(--text-dim)' },
  txLink: { color: 'var(--accent)', textDecoration: 'none', display: 'inline-block' },
};
